import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, Tooltip, Typography,
} from '@mui/material';
import HistoryBookIcon from '../icons/HistoryBookIcon';
import { fetchPosSizingContextUpdates } from '../../utils/ApiCall';
import { appBarActionButtonSx } from '../../styles/msiAppBarStyles';
import {
    HistoryCategoryChip, HistoryStatusIcon, historyLedgerRowSx,
} from '../shared/HistoryLedgerPrimitives';

const ORIGIN_LABELS = { automatic: 'Automatic', manual: 'Manual', portfolio_import: 'Portfolio update' };

export default function PosSizingContextUpdates({ userUid, publicationVersion = 0 }) {
    const [open, setOpen] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState({ loading: false, events: [], error: null });
    useEffect(() => {
        if (!open || !userUid) return undefined;
        let current = true;
        setState({ loading: true, events: [], error: null });
        void fetchPosSizingContextUpdates().then((result) => {
            if (!Array.isArray(result?.events)) throw new Error('Invalid update history.');
            if (current) setState({ loading: false, events: result.events.slice(0, 100), error: null });
        }).catch(() => {
            if (current) setState({ loading: false, events: [], error: 'Recent context updates could not be loaded.' });
        });
        return () => { current = false; };
    }, [attempt, open, publicationVersion, userUid]);
    useEffect(() => { setOpen(false); }, [userUid]);
    return (
        <>
            <Tooltip title="Recent context updates">
                <span>
                    <IconButton size="small" aria-label="Recent context updates" onClick={() => setOpen(true)} disabled={!userUid} sx={appBarActionButtonSx}>
                        <HistoryBookIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Recent context updates</DialogTitle>
                <DialogContent dividers>
                    {state.loading ? <Box role="status" display="flex" gap={1} alignItems="center"><CircularProgress size={18} />Loading updates…</Box> : null}
                    {state.error ? <Alert severity="error" action={<Button color="inherit" onClick={() => setAttempt(value => value + 1)}>Retry</Button>}>{state.error}</Alert> : null}
                    {!state.loading && !state.error && state.events.length === 0 ? <Typography color="text.secondary">No context updates have been recorded yet.</Typography> : null}
                    {!state.loading && !state.error ? state.events.map((event, index) => (
                        <Box key={event.event_id} sx={historyLedgerRowSx({ last: index === state.events.length - 1 })} display="flex" gap={1} alignItems="flex-start">
                            <HistoryStatusIcon kind="success" label="Context updated" color="success.main" />
                            <Box flex={1} minWidth={0}>
                                <Typography variant="body2">{event.summary}</Typography>
                                <Typography component="time" dateTime={event.published_at} variant="caption" color="text.secondary">
                                    {new Date(event.published_at).toLocaleString(undefined, { timeZoneName: 'short' })}
                                </Typography>
                            </Box>
                            <HistoryCategoryChip label={ORIGIN_LABELS[event.origin] ?? 'Update'} />
                        </Box>
                    )) : null}
                </DialogContent>
                <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </>
    );
}
